<template>
  <!-- Caricamento di una tabella: righe finte allineate alle colonne vere,
       con barre che si accendono riga per riga dall'alto. Si usa nello slot
       #table-busy delle b-table (registrato globalmente in main.js). -->
  <div class="table-skeleton" role="status">
    <span class="sr-only">Operazione in corso...</span>
    <div
      v-for="row in rows"
      :key="row"
      class="table-skeleton__row"
      :style="{ '--i': row }"
      aria-hidden="true"
    >
      <div
        v-for="(column, index) in columns"
        :key="index"
        class="table-skeleton__cell"
        :class="{ 'is-right': column.right }"
        :style="{ width: `${column.width}px` }"
      >
        <span
          v-if="column.bar"
          class="table-skeleton__bar"
          :style="{ width: `${barWidth(row, index, column)}px` }"
        ></span>
      </div>
    </div>
  </div>
</template>

<script>
// Altezza di una riga finta: più alta delle righe vere (30-40px), con barre
// più spesse, così il caricamento si legge meglio.
const ROW_HEIGHT = 60;

export default {
  data() {
    return {
      rows: 0,
      columns: [],
    };
  },
  mounted() {
    this.measure();
    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(() => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(this.measure);
      });
      this.observer.observe(this.$el.parentElement);
    }
  },
  beforeDestroy() {
    cancelAnimationFrame(this.frame);
    this.observer?.disconnect();
  },
  methods: {
    // Colonne prese dalla testata: stessa larghezza, allineamento a destra
    // per gli importi, nessuna barra per spunte e azioni.
    measure() {
      const cell = this.$el?.parentElement;
      const table = this.$el?.closest("table");
      if (!cell || !table) return;
      // Solo le colonne visibili (alcune tabelle ne nascondono con d-none).
      const headers = [
        ...table.querySelectorAll(":scope > thead > tr:last-child > th"),
      ].filter((th) => th.offsetWidth > 0);
      this.columns = headers.length
        ? headers.map((th) => ({
            width: th.getBoundingClientRect().width,
            right:
              th.classList.contains("text-right") ||
              getComputedStyle(th).textAlign === "right",
            bar: !(
              th.classList.contains("check") ||
              th.classList.contains("actions-head")
            ),
          }))
        : [{ width: cell.getBoundingClientRect().width, bar: true }];
      this.rows = Math.max(1, Math.floor(cell.clientHeight / ROW_HEIGHT));
    },
    // Lunghezze diverse ma sempre uguali per la stessa cella, così le barre
    // non cambiano a ogni ricalcolo.
    barWidth(row, index, column) {
      const ratio = 0.45 + ((row * 7 + index * 13) % 9) / 20;
      return Math.max(12, Math.round((column.width - 16) * ratio));
    },
  },
};
</script>

<style lang="scss" scoped>
// width: 0 + min-width: 100%: le celle a larghezza fissa non allargano la
// tabella, che può restringersi (e far ricalcolare le colonne) anche durante
// il caricamento.
.table-skeleton {
  display: flex;
  flex-direction: column;
  width: 0;
  min-width: 100%;
  overflow: hidden;
  text-align: left;
}

.table-skeleton__row {
  display: flex;
  align-items: center;
  flex: 0 0 60px;

  // nth-of-type: il primo figlio è il testo per gli screen reader.
  &:nth-of-type(odd) {
    background: var(--color-row-odd);
  }

  &:nth-of-type(even) {
    background: var(--color-row-even);
  }
}

.table-skeleton__cell {
  display: flex;
  flex: 0 0 auto;
  padding: 0 8px;

  &.is-right {
    justify-content: flex-end;
  }
}

// L'onda: ogni riga si accende con 90ms di ritardo sulla precedente. Si
// anima solo l'opacità (della barra grigia e del velo azzurro sopra), che il
// browser compone senza ridisegnare le centinaia di barre.
.table-skeleton__bar {
  position: relative;
  height: 15px;
  border-radius: 7.5px;
  background: rgba(17, 24, 28, 0.14);
  animation: table-skeleton-pulse 1.6s ease-in-out infinite;
  animation-delay: calc(var(--i) * 90ms);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: color-mix(in srgb, var(--lisaweb) 28%, white);
    opacity: 0;
    animation: table-skeleton-tint 1.6s ease-in-out infinite;
    animation-delay: inherit;
  }
}

@keyframes table-skeleton-pulse {
  0%,
  100% {
    opacity: 0.55;
  }

  40% {
    opacity: 1;
  }
}

@keyframes table-skeleton-tint {
  0%,
  100% {
    opacity: 0;
  }

  40% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .table-skeleton__bar,
  .table-skeleton__bar::after {
    animation: none;
  }
}
</style>
